import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, Mail } from "lucide-react";
import { Reveal, Btn, Overline, MaskedLine } from "@/components/motion-bits";
import { Label, Input, TextArea } from "@/components/Field";
import mapImg from "@/assets/contact_map.jpg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", phone: "", company: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message received — we'll get back to you.", { duration: 5000 });
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Try again or call 833-V1aduct (833-812-3828).");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="px-6 lg:px-10 py-16 md:py-24">
        <div className="max-w-7xl mx-auto">
          <Overline className="text-brand-red">
            <MaskedLine delay={0.1}>Contact</MaskedLine>
          </Overline>
          <h1 className="mt-4 font-display uppercase tracking-tight leading-[0.9] text-[clamp(3rem,8vw,7rem)]">
            <MaskedLine delay={0.24}>Talk to</MaskedLine>
            <MaskedLine delay={0.38}>
              <span className="text-brand-teal">the viaduct.</span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-ink/75">
              For more info call 833-V1aduct (833-812-3828) — dispatch answers, every time.
            </p>
          </Reveal>
          <Reveal delay={0.58}>
            <a
              href="tel:+18338123828"
              data-testid="contact-phone-link"
              className="mt-8 inline-flex items-center gap-4 border-[3px] border-ink bg-brand-red text-cream px-6 py-4 font-display uppercase tracking-wide text-2xl md:text-4xl shadow-block transition-[transform,box-shadow] duration-200 hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-[2px_2px_0_#111]"
            >
              <Phone size={28} aria-hidden="true" /> 833-V1ADUCT
            </a>
            <p className="mt-3 text-lg font-semibold text-ink/60">833-812-3828</p>
            <a
              href="mailto:office@memphisviaduct.com"
              data-testid="contact-email-link"
              className="mt-6 inline-flex items-center gap-2 text-xl font-semibold text-ink underline decoration-brand-red decoration-[3px] underline-offset-4 transition-colors duration-200 hover:text-brand-red"
            >
              <Mail size={20} aria-hidden="true" /> office@memphisviaduct.com
            </a>
          </Reveal>
        </div>
      </section>

      {/* Form + map */}
      <section className="bg-paper border-y-[3px] border-ink px-6 lg:px-10 py-20 md:py-28">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <Overline className="text-brand-red">Send a message</Overline>
              <h2 className="mt-3 font-display uppercase tracking-tight text-3xl md:text-5xl">
                Freight, questions, anything.
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <form onSubmit={submit} data-testid="contact-form" className="mt-8 border-[3px] border-ink bg-cream p-6 md:p-10 shadow-blocklg">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="contact-name">Name</Label>
                    <Input id="contact-name" name="name" required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="contact-email">Email</Label>
                    <Input id="contact-email" name="email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="contact-email-input" />
                  </div>
                  <div>
                    <Label htmlFor="contact-phone">Phone (optional)</Label>
                    <Input id="contact-phone" name="phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="(901) 555-0123" data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <Label htmlFor="contact-company">Company (optional)</Label>
                    <Input id="contact-company" name="company" value={form.company} onChange={set("company")} placeholder="Company name" data-testid="contact-company-input" />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="contact-message">Message</Label>
                    <TextArea id="contact-message" name="message" required value={form.message} onChange={set("message")} placeholder="Tell us what needs to move…" data-testid="contact-message-input" />
                  </div>
                </div>

                {sent && (
                  <div className="mt-6 border-[3px] border-ink bg-brand-gold/20 p-4 font-semibold" data-testid="contact-success-panel">
                    Message received — we'll get back to you. Need an answer now? Call
                    833-V1aduct (833-812-3828).
                  </div>
                )}

                <div className="mt-8">
                  <Btn type="submit" variant="red" testid="contact-submit-button" disabled={sending}>
                    {sending ? "Sending…" : "Send Message"}
                  </Btn>
                </div>
              </form>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="rotate-1 border-[3px] border-ink bg-cream p-2 shadow-blocklg">
                <img
                  src={mapImg}
                  alt="Illustrated map of Memphis at the I-55 and I-40 crossing on the Mississippi River"
                  className="w-full aspect-square object-cover"
                  data-testid="contact-map-illustration"
                />
                <p className="px-2 py-3 text-center font-display uppercase tracking-wide text-lg text-brand-teal">
                  The crossing point · Memphis, TN
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.22}>
              <div className="mt-8 border-[3px] border-ink bg-ink text-cream p-6 md:p-8 shadow-block" data-testid="contact-location-card">
                <p className="flex items-start gap-3">
                  <MapPin size={24} className="mt-1 shrink-0 text-brand-gold" aria-hidden="true" />
                  <span className="text-lg leading-relaxed">
                    On the bank of the Mississippi River, at the I-55 &amp; I-40 crossing point —
                    Memphis, Tennessee.
                  </span>
                </p>
                <p className="mt-5 flex items-center gap-3">
                  <Phone size={20} className="shrink-0 text-brand-gold" aria-hidden="true" />
                  <a href="tel:+18338123828" data-testid="contact-location-phone-link" className="font-display uppercase text-xl tracking-wide transition-colors duration-200 hover:text-brand-gold">
                    833-V1aduct · 833-812-3828
                  </a>
                </p>
                <p className="mt-4 flex items-center gap-3">
                  <Mail size={20} className="shrink-0 text-brand-gold" aria-hidden="true" />
                  <a href="mailto:office@memphisviaduct.com" data-testid="contact-location-email-link" className="font-semibold tracking-wide transition-colors duration-200 hover:text-brand-gold">
                    office@memphisviaduct.com
                  </a>
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
